import gsap from "gsap";

import { CustomEase } from "gsap/all";
import { SlotReelSymbol } from "../../../../constants/enum/slot";

gsap.registerPlugin(CustomEase);

const SpeedUpDuration = 0.4;
const SpinningSubDuration = 0.3;
const SpinSlowDownDuration = 0.5;

export const startSpeedUpAnimation = (
  tl: React.MutableRefObject<gsap.core.Timeline | undefined>,
  reelRef: React.MutableRefObject<HTMLDivElement | undefined>,
  reelContainerRef: React.MutableRefObject<HTMLDivElement | undefined>,
  cb: () => void
) => {
  tl.current?.clear();
  tl.current = gsap.timeline();

  // Ensure necessary refs are available
  if (!reelRef.current || !reelContainerRef.current) {
    console.error("Reel or reel container references are missing.");
    return;
  }

  // Calculate the movement distance for the speed-up animation
  const reelContainerHeight = reelContainerRef.current.clientHeight;
  const moveDistanceY = reelContainerHeight * 2;

  // Define custom ease for reusability
  const customEase = CustomEase.create(
    "custom",
    "M0,0 C0,0 0.268,-0.162 0.372,-0.162 0.512,-0.162 0.663,0.411 0.827,0.672 0.981,0.919 1,1 1,1 "
  );

  // console.log("Starting SpeedUp animation");

  tl.current
    .to(
      reelRef.current,
      {
        y: `+=${moveDistanceY}`, // Move reels by calculated distance
        duration: SpeedUpDuration,
        ease: customEase,
        onComplete: () => {
          // console.log("Completed SpeedUp animation");
        },
      },
      0 // Start immediately
    )
    .call(
      () => {
        // Start spinning animation after speed-up finishes
        cb();
      },
      [], // Pass empty arguments to the callback
      SpeedUpDuration // Delay the callback to match the speed-up duration
    )
    .pause(); // Pause the timeline initially

  // Play the timeline
  tl.current.play();
};

export const startSpinningAnimation = (
  tl: React.MutableRefObject<gsap.core.Timeline | undefined>,
  reelRef: React.MutableRefObject<HTMLDivElement | undefined>,
  reelContainerRef: React.MutableRefObject<HTMLDivElement | undefined>,
  spinCount: number,
  cb: () => void
) => {
  tl.current?.clear();
  tl.current = gsap.timeline();

  if (!reelRef.current || !reelContainerRef.current) return;

  //  console.log("Starting spinning animation");
  const reelHeight = reelRef.current.clientHeight;

  tl.current
    .set(reelRef.current, { y: 0 })
    .to(
      reelRef.current,
      { y: reelHeight, duration: SpinningSubDuration, ease: "linear" },
      0
    )
    .repeat(spinCount);

  tl.current.play();

  cb();
};

export const startSpeedDownAnimation = (
  tl: React.MutableRefObject<gsap.core.Timeline | undefined>,
  reelRef: React.MutableRefObject<HTMLDivElement | undefined>,
  reelContainerRef: React.MutableRefObject<HTMLDivElement | undefined>,
  reelSymbols: SlotReelSymbol[],
  targetSymbol: SlotReelSymbol,
  onComplete: () => void
) => {
  tl.current?.clear();
  tl.current = gsap.timeline();
  if (!reelRef.current || !reelContainerRef.current) return;
  const thirdSymbolIndex = reelSymbols.findIndex((_: SlotReelSymbol, index) => {
    // Count occurrences of the target symbol
    const symbol = targetSymbol; // Replace with the symbol you're looking for
    let occurrenceCount = 0;

    for (let i = 0; i <= index; i++) {
      if (reelSymbols[i] === symbol) {
        occurrenceCount++;
      }
    }

    // Return true when the third occurrence is found
    return occurrenceCount === 3;
  });

  //  console.log(`Starting slow down animation : ${thirdSymbolIndex}`);

  // Reel symbol width is same as height
  const height = reelRef.current.clientWidth;

  tl.current
    .set(reelRef.current, { y: 0 })
    .to(
      reelRef.current,
      {
        y: (43 - thirdSymbolIndex) * height - height + 4,
        duration: SpinSlowDownDuration,
        ease: "power2.out",
        onComplete: () => {
          onComplete();
          //      console.log("Completing slow down animation");
        },
      },
      0
    )
    .pause();
  tl.current.play();
};
